import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiHeart, FiMessageCircle, FiSend, FiBookmark, FiMoreHorizontal, FiTrash2 } from 'react-icons/fi';
import { format } from 'timeago.js';
import { postsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function PostCard({ post, onDelete }) {
  const { user }              = useAuth();
  const [liked, setLiked]     = useState(!!post.is_liked);
  const [saved, setSaved]     = useState(!!post.is_saved);
  const [likes, setLikes]     = useState(+post.likes_count || 0);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [loadingCmts, setLoadingCmts]   = useState(false);
  const [showMenu, setShowMenu]         = useState(false);
  const [popping, setPopping]           = useState(false);

  const isOwner = +user?.id === +post.user_id;

  const handleLike = async () => {
    const was = liked;
    setLiked(!was);
    setLikes(l => was ? l - 1 : l + 1);
    if (!was) { setPopping(true); setTimeout(() => setPopping(false), 320); }
    try { await postsAPI.likePost(post.id); }
    catch { setLiked(was); setLikes(l => was ? l + 1 : l - 1); }
  };

  const handleSave = async () => {
    const was = saved;
    setSaved(!was);
    try {
      await postsAPI.savePost(post.id);
      toast.success(was ? 'Removed from saved' : 'Post saved!');
    } catch { setSaved(was); }
  };

  const toggleComments = async () => {
    if (!showComments && comments.length === 0) {
      setLoadingCmts(true);
      try {
        const res = await postsAPI.getComments(post.id);
        setComments(res.data.comments || []);
      } catch {}
      finally { setLoadingCmts(false); }
    }
    setShowComments(p => !p);
  };

  const handleComment = async e => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      await postsAPI.addComment(post.id, comment.trim());
      setComments(p => [...p, {
        id: Date.now(), content: comment.trim(),
        username: user.username, avatar: user.avatar,
        created_at: new Date().toISOString()
      }]);
      setComment('');
    } catch { toast.error('Failed to comment.'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await postsAPI.deletePost(post.id);
      toast.success('Post deleted.');
      onDelete?.(post.id);
    } catch { toast.error('Failed.'); }
    setShowMenu(false);
  };

  const avatarPh = (name, size) => (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'linear-gradient(135deg,#E91E8C,#7B2FF7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: size * 0.36, flexShrink: 0
    }}>{name?.[0]?.toUpperCase()}</div>
  );

  return (
    <article className="post-card card fade-in">
      {/* Header */}
      <div className="post-header">
        <Link to={`/profile/${post.user_id}`} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', textDecoration: 'none' }}>
          {post.avatar
            ? <img src={post.avatar} className="avatar" style={{ width: 'clamp(32px,5vw,40px)', height: 'clamp(32px,5vw,40px)' }} alt="" />
            : avatarPh(post.full_name || post.username, 38)
          }
          <div>
            <p className="font-bold" style={{ fontSize: 'var(--fs-sm)' }}>
              {post.username}
              {post.is_verified && <span style={{ color: 'var(--primary)', marginLeft: 4 }}>✓</span>}
            </p>
            <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-3)' }}>{format(post.created_at)}</p>
          </div>
        </Link>

        <div style={{ position: 'relative' }}>
          <button className="btn btn-icon btn-ghost" onClick={() => setShowMenu(p => !p)}>
            <FiMoreHorizontal size={20} />
          </button>
          {showMenu && (
            <div className="dropdown-menu">
              {isOwner && (
                <button className="dropdown-item danger" onClick={handleDelete}>
                  <FiTrash2 size={14} /> Delete
                </button>
              )}
              <button className="dropdown-item" onClick={() => {
                navigator.clipboard.writeText(window.location.origin + `/post/${post.id}`);
                toast.success('Link copied!');
                setShowMenu(false);
              }}>
                <FiSend size={14} /> Copy link
              </button>
              <button className="dropdown-item" onClick={() => setShowMenu(false)}>Cancel</button>
            </div>
          )}
        </div>
      </div>

      {/* Media */}
      <div className="post-media">
        {post.media_type === 'video'
          ? <video src={post.media_url} controls playsInline style={{ width: '100%', maxHeight: 'clamp(260px,55vw,520px)', objectFit: 'cover' }} />
          : <img src={post.media_url} alt={post.caption || 'Post'} loading="lazy"
              style={{ width: '100%', maxHeight: 'clamp(260px,55vw,520px)', objectFit: 'cover' }} />
        }
      </div>

      {/* Actions */}
      <div className="post-actions">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(2px,1vw,6px)' }}>
          <button
            onClick={handleLike}
            className={'btn btn-icon' + (popping ? ' pop' : '')}
            style={{ color: liked ? 'var(--danger)' : 'var(--text-2)' }}
          >
            <FiHeart size={22} fill={liked ? 'var(--danger)' : 'none'} stroke={liked ? 'var(--danger)' : 'currentColor'} />
          </button>
          <button className="btn btn-icon" style={{ color: 'var(--text-2)' }} onClick={toggleComments}>
            <FiMessageCircle size={22} />
          </button>
          <button className="btn btn-icon" style={{ color: 'var(--text-2)' }}>
            <FiSend size={20} />
          </button>
        </div>
        <button onClick={handleSave} className="btn btn-icon" style={{ color: 'var(--text-2)' }}>
          <FiBookmark size={22} fill={saved ? 'var(--text)' : 'none'} />
        </button>
      </div>

      {/* Likes */}
      {likes > 0 && (
        <p className="font-bold" style={{ padding: '0 var(--sp-3) var(--sp-1)', fontSize: 'var(--fs-sm)' }}>
          {likes.toLocaleString()} {likes === 1 ? 'like' : 'likes'}
        </p>
      )}

      {/* Caption */}
      {post.caption && (
        <p style={{ padding: '0 var(--sp-3) var(--sp-2)', fontSize: 'var(--fs-sm)', lineHeight: 1.55 }}>
          <Link to={`/profile/${post.user_id}`} className="font-bold" style={{ marginRight: 6 }}>{post.username}</Link>
          {post.caption}
        </p>
      )}

      {/* Comment toggle */}
      {(+post.comments_count > 0 || comments.length > 0) && (
        <button onClick={toggleComments}
          style={{ padding: '0 var(--sp-3) var(--sp-2)', display: 'block', textAlign: 'left', color: 'var(--text-3)', fontSize: 'var(--fs-xs)', background: 'none', border: 'none', cursor: 'pointer' }}>
          {showComments ? 'Hide comments' : `View all ${post.comments_count} comments`}
        </button>
      )}

      {/* Comments */}
      {showComments && (
        <div style={{ maxHeight: 200, overflowY: 'auto', padding: '4px 0' }}>
          {loadingCmts
            ? <div style={{ padding: '10px var(--sp-3)' }}><span className="spinner" /></div>
            : comments.map(c => (
                <div key={c.id} style={{ display: 'flex', gap: 8, padding: '5px var(--sp-3)', alignItems: 'flex-start' }}>
                  {c.avatar
                    ? <img src={c.avatar} className="avatar" style={{ width: 26, height: 26, flexShrink: 0, marginTop: 2 }} alt="" />
                    : <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'linear-gradient(135deg,#E91E8C,#7B2FF7)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 10, flexShrink: 0, marginTop: 2 }}>
                        {c.username?.[0]?.toUpperCase()}
                      </div>
                  }
                  <p style={{ fontSize: 'var(--fs-xs)', lineHeight: 1.55 }}>
                    <span className="font-bold">{c.username}</span>{' '}{c.content}
                  </p>
                </div>
              ))
          }
        </div>
      )}

      {/* Add comment */}
      <form onSubmit={handleComment} className="comment-form">
        <input className="comment-input" placeholder="Add a comment…"
          value={comment} onChange={e => setComment(e.target.value)} />
        {comment && (
          <button type="submit" style={{ color: 'var(--primary)', fontWeight: 700, fontSize: 'var(--fs-sm)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
            Post
          </button>
        )}
      </form>
    </article>
  );
}
